import {
  Badge,
  Box,
  Button,
  Flex,
  SkeletonText,
  Tab,
  TabList,
  Table,
  TableContainer,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router"
import { useEffect } from "react"
import { z } from "zod"
import Select from "@/components/Common/AppSelect"
import { type UserPublic, UsersService } from "../../../client"
import AddUser from "../../../components/Admin/AddUser"
import { PaginationFooter } from "../../../components/Common/PaginationFooter.tsx"
import { WmsListToolbar } from "../../../components/WMS/shared/WmsListToolbar.tsx"
import { WmsPageHeader } from "../../../components/WMS/shared/WmsPageHeader.tsx"
import {
  WmsPagePanel,
  WmsPageShell,
} from "../../../components/WMS/shared/WmsPageShell.tsx"
import { WmsTableListCard } from "../../../components/WMS/shared/WmsTableListCard.tsx"

const usersSearchSchema = z.object({
  page: z.number().catch(1),
  pageSize: z.number().catch(10),
})

export const Route = createFileRoute("/_layout/admin/")({
  component: Admin,
  validateSearch: (search) => {
    const parsed = usersSearchSchema.parse(search)
    return {
      ...parsed,
      pageSize: PAGE_SIZE_OPTIONS.includes(
        parsed.pageSize as (typeof PAGE_SIZE_OPTIONS)[number],
      )
        ? parsed.pageSize
        : DEFAULT_PAGE_SIZE,
    }
  },
})

const DEFAULT_PAGE_SIZE = 10
const PAGE_SIZE_OPTIONS = [10, 20, 30] as const

function getUsersQueryOptions({
  page,
  pageSize,
}: {
  page: number
  pageSize: number
}) {
  return {
    queryFn: () =>
      UsersService.readUsers({
        skip: (page - 1) * pageSize,
        limit: pageSize,
        scope: "active",
      }),
    queryKey: ["users", { page, pageSize, scope: "active" }],
  }
}

function getDeletedUsersQueryOptions({
  page,
  pageSize,
}: {
  page: number
  pageSize: number
}) {
  return {
    queryFn: () =>
      UsersService.readUsers({
        skip: (page - 1) * pageSize,
        limit: pageSize,
        scope: "deleted",
      }),
    queryKey: ["users", { page, pageSize, scope: "deleted" }],
  }
}

function UsersTable({ scope }: { scope: "active" | "deleted" }) {
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const { page, pageSize } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setPage = (page: number) =>
    navigate({
      search: (current) => ({ ...current, page }),
    })

  const {
    data: users,
    isPending,
    isPlaceholderData,
  } = useQuery({
    ...(scope === "deleted"
      ? getDeletedUsersQueryOptions({ page, pageSize })
      : getUsersQueryOptions({ page, pageSize })),
    placeholderData: (prevData) => prevData,
  })

  const rows = users?.data ?? []
  const hasNextPage = !isPlaceholderData && rows.length === pageSize
  const hasPreviousPage = page > 1
  const startRow = rows.length > 0 ? (page - 1) * pageSize + 1 : 0
  const endRow = rows.length > 0 ? startRow + rows.length - 1 : 0
  const theadBg = useColorModeValue("gray.100", "gray.700")
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const zebraBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50")
  const mutedText = useColorModeValue("gray.500", "gray.400")

  useEffect(() => {
    if (hasNextPage) {
      queryClient.prefetchQuery(
        scope === "deleted"
          ? getDeletedUsersQueryOptions({ page: page + 1, pageSize })
          : getUsersQueryOptions({ page: page + 1, pageSize }),
      )
    }
  }, [page, pageSize, queryClient, hasNextPage, scope])

  return (
    <WmsTableListCard
      isLoading={false}
      error={undefined}
      errorMessage=""
      isEmpty={false}
      footer={
        <Flex
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={3}
          px={4}
          py={4}
          flexShrink={0}
        >
          <Text fontSize="sm" color={mutedText}>
            {rows.length > 0
              ? `Pokazano ${startRow}-${endRow}`
              : "Pokazano 0-0"}
          </Text>
          <PaginationFooter
            mt={0}
            onChangePage={setPage}
            page={page}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
          />
        </Flex>
      }
    >
      <TableContainer width="100%" flex="1" overflowY="auto">
        <Table
          size="sm"
          sx={{
            tableLayout: "fixed",
          }}
        >
          <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th>Imię i nazwisko</Th>
              <Th>Akronim</Th>
              <Th>Email</Th>
              <Th>Role</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          {isPending ? (
            <Tbody>
              <Tr>
                {new Array(5).fill(null).map((_, index) => (
                  <Td key={index}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Td>
                ))}
              </Tr>
            </Tbody>
          ) : (
            <Tbody>
              {rows.map((user, idx) => (
                <Tr
                  key={user.ID}
                  bg={idx % 2 === 0 ? "transparent" : zebraBg}
                  _hover={{ bg: hoverBg }}
                  cursor="pointer"
                  onClick={() =>
                    navigate({
                      to: `/admin/users/${user.ID}`,
                      search: { page, pageSize },
                    })
                  }
                >
                  <Td isTruncated maxWidth="150px" fontWeight="medium">
                    <Flex align="center" gap={2}>
                      <Box
                        color={!user.fullName ? "ui.dim" : "main.accent"}
                        noOfLines={1}
                        fontSize="sm"
                      >
                        {user.fullName || "Brak"}
                      </Box>
                      {currentUser?.ID === user.ID && (
                        <Badge colorScheme="teal" borderRadius="full">
                          Ty
                        </Badge>
                      )}
                    </Flex>
                  </Td>
                  <Td
                    isTruncated
                    maxWidth="150px"
                    color={mutedText}
                    fontSize="sm"
                  >
                    {user.acronym}
                  </Td>
                  <Td isTruncated maxWidth="150px" fontSize="sm">
                    {user.email}
                  </Td>
                  <Td fontSize="sm">
                    {Array.isArray(user.roles) && user.roles.length > 0 ? (
                      <Flex wrap="wrap" gap={1}>
                        <Badge
                          colorScheme="green"
                          variant="subtle"
                          borderRadius="full"
                        >
                          {user.roles[0].code} ({user.roles[0].level})
                        </Badge>
                      </Flex>
                    ) : (
                      <Badge
                        colorScheme="gray"
                        variant="outline"
                        borderRadius="full"
                      >
                        Brak ról
                      </Badge>
                    )}
                  </Td>
                  <Td fontSize="sm">
                    <Flex gap={2}>
                      <Box
                        w="2"
                        h="2"
                        borderRadius="50%"
                        bg={user.isActive ? "ui.success" : "ui.danger"}
                        alignSelf="center"
                      />
                      {user.isActive ? "Aktywny" : "Nieaktywny"}
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>
      </TableContainer>
    </WmsTableListCard>
  )
}

function Admin() {
  const navigate = useNavigate()
  const { pageSize } = Route.useSearch()
  const addUserModal = useDisclosure()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isIndex = pathname === "/admin"
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")

  const queryClient = useQueryClient()
  const me = queryClient.getQueryData<UserPublic>(["currentUser"])

  return (
    <WmsPageShell>
      {isIndex ? (
        <>
          <WmsPageHeader
            title="Zarządzanie użytkownikami"
            description="Zarządzaj kontami, rolami i dostępem do modułów systemu."
            gradient="linear(to-r, slateblue, #2B6CB0)"
            badge={`Zalogowany: ${me?.email ?? "—"}`}
          />

          <WmsPagePanel bg={panelBg} borderColor={panelBorder}>
            <WmsListToolbar
              filters={<Text fontWeight="semibold">Zespół i uprawnienia</Text>}
              actionsWrap="nowrap"
              actions={
                <>
                  <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                    Wiersze:
                  </Text>
                  <Select
                    size="sm"
                    w="96px"
                    value={String(pageSize)}
                    onChange={(e) =>
                      navigate({
                        to: "/admin",
                        search: {
                          page: 1,
                          pageSize: Number(e.target.value),
                        },
                      })
                    }
                    borderRadius="xl"
                  >
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                  <Button
                    size="sm"
                    colorScheme="green"
                    borderRadius="xl"
                    onClick={addUserModal.onOpen}
                  >
                    Dodaj użytkownika
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    borderRadius="xl"
                    onClick={() =>
                      navigate({ to: "/admin/roles", search: { page: 1 } })
                    }
                  >
                    Zarządzaj rolami
                  </Button>
                </>
              }
            />
            <Tabs
              variant="enclosed"
              colorScheme="green"
              isLazy
              mt={{ base: 4, lg: 3 }}
              display="flex"
              flexDirection="column"
              minH={0}
              flex="1"
              overflow="hidden"
            >
              <TabList flexShrink={0}>
                <Tab>Aktywni</Tab>
                <Tab>Usunięci</Tab>
              </TabList>
              <TabPanels minH={0} flex="1" overflow="hidden">
                <TabPanel
                  px={0}
                  pt={{ base: 4, lg: 2 }}
                  h="full"
                  minH={0}
                  display="flex"
                  flexDirection="column"
                >
                  <UsersTable scope="active" />
                </TabPanel>
                <TabPanel
                  px={0}
                  pt={{ base: 4, lg: 2 }}
                  h="full"
                  minH={0}
                  display="flex"
                  flexDirection="column"
                >
                  <UsersTable scope="deleted" />
                </TabPanel>
              </TabPanels>
            </Tabs>
            <AddUser
              isOpen={addUserModal.isOpen}
              onClose={addUserModal.onClose}
            />
          </WmsPagePanel>
        </>
      ) : (
        <Outlet />
      )}
    </WmsPageShell>
  )
}
